import { Button, Group, Modal, NativeSelect, Textarea } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import {
  defaultLineValues,
  lineChoices,
  lineSelectionNeedsReason,
  lineSelectionSummary,
  toLineSelection,
} from './line-selection.js';
import classes from './Purchasing.module.css';
import { formatAmount, formatQuantity } from './requisitions.js';
import type { RfqSource } from './source.js';
import type { RfqDetail } from './types.js';

/**
 * Satır satır seçim (04.11 notları D4; ADR 0166 §2): her RFQ kalemi için tedarikçi ya da "açık
 * bırak". Seçenek yalnız fiyatlı ya da ikame yanıttır; reddedilen ve fiyatsız satır seçenek
 * değildir, teklifin öteki satırları seçilebilir. Satırın en iyi fiyatı dışındaki seçim gerekçe
 * ister. Her tedarikçiye bir taslak PO açılır; PO onay zincirinden geçer. Başka ödüldeki kalem
 * kilitlidir.
 */
export function LineSelectionDialog({
  detail,
  source,
  quotationId,
  onClose,
  onSelected,
}: {
  detail: RfqDetail;
  source: RfqSource;
  /** Eksik teklifin kısayolu: yalnız bu teklifin fiyatlı satırları seçili açılır. */
  quotationId: string | null;
  onClose: () => void;
  onSelected: (next: RfqDetail, orders: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.detail.lineSelection.${key}`, options);
  const money = (value: number | null) => formatAmount(value, locale);
  const choices = lineChoices(detail);
  // Başka ödüldeki kalemler (ADR 0111 §D): seçilemez, kimde olduğu yazılır
  const [awarded, setAwarded] = useState<ReadonlyMap<string, string>>(new Map());
  const [values, setValues] = useState<Record<string, string>>(() =>
    defaultLineValues(detail, { quotationId }),
  );
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!source.awards || (detail.awardCount ?? 0) === 0) return;
    let live = true;
    void source.awards.get(detail.id).then(
      (view) => {
        if (!live) return;
        const taken = new Map(
          view.lines.flatMap((line) =>
            line.awardId ? [[line.rfqLineId, line.supplierName ?? ''] as const] : [],
          ),
        );
        setAwarded(taken);
        setValues(defaultLineValues(detail, { quotationId, awarded: new Set(taken.keys()) }));
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [source, detail, quotationId]);

  const needsReason = lineSelectionNeedsReason(detail, values);
  const summary = lineSelectionSummary(detail, values);

  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      title={k('title')}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          const selection = toLineSelection(detail, values, reason);
          if (selection.lines.length === 0) return setProblem('none');
          if (needsReason && !selection.reason) return setProblem('reasonRequired');
          if (!source.selectLines) return;
          setBusy(true);
          setProblem(null);
          try {
            onSelected(await source.selectLines(detail.id, selection), summary.length);
          } catch (failure) {
            setProblem(`error:${errorCode(failure)}`);
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className={classes.note}>{k('body')}</p>
        <section
          className={table.tableWrap}
          aria-label={k('items')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.table} ${classes.lineTable}`}>
            <thead>
              <tr>
                <th scope="col" className={classes.index}>
                  {t('purchasing.rfq.detail.columns.no')}
                </th>
                <th scope="col">{t('purchasing.rfq.detail.columns.material')}</th>
                <th scope="col" data-numeric>
                  {t('purchasing.rfq.detail.columns.qty')}
                </th>
                <th scope="col">{k('supplier')}</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => {
                const own = choices.get(line.id) ?? [];
                const holder = awarded.get(line.id);
                return (
                  <tr key={line.id}>
                    <td className={classes.index}>{line.lineNo}</td>
                    <td className={classes.wrap}>{line.name}</td>
                    <td data-numeric>
                      {formatQuantity(line.quantity, locale)} {line.unit}
                    </td>
                    <td>
                      {holder !== undefined ? (
                        <span className={classes.muted}>{k('awarded', { supplier: holder })}</span>
                      ) : (
                        <NativeSelect
                          aria-label={k('choiceOf', { name: line.name })}
                          value={values[line.id] ?? ''}
                          onChange={(event) =>
                            setValues((v) => ({ ...v, [line.id]: event.currentTarget.value }))
                          }
                          data={[
                            { value: '', label: own.length ? k('leaveOpen') : k('notQuoted') },
                            ...own.map((choice) => ({
                              value: choice.value,
                              label: [
                                choice.supplierName,
                                money(choice.unitPrice),
                                choice.kind === 'substitute' ? k('substitute') : null,
                                choice.free ? k('free') : null,
                                choice.best ? k('best') : null,
                              ]
                                .filter(Boolean)
                                .join(' · '),
                            })),
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
        {summary.length > 0 ? (
          <ul className={classes.selectionSummary} aria-live="polite">
            {summary.map((group) => (
              <li key={group.supplierName}>
                {k('summary', {
                  supplier: group.supplierName,
                  count: group.count,
                  total: `${money(group.total)} ${detail.currency}`,
                })}
              </li>
            ))}
          </ul>
        ) : null}
        {needsReason ? (
          <Textarea
            label={k('reason')}
            description={k('reasonBody')}
            required
            autosize
            minRows={2}
            maxLength={1000}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            error={problem === 'reasonRequired' ? k('reasonRequired') : undefined}
            mt="sm"
          />
        ) : null}
        {problem === 'none' ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {k('none')}
          </p>
        ) : null}
        {problem?.startsWith('error:') ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {t(`purchasing.errors.${problem.slice(6)}`, {
              defaultValue: t('purchasing.errors.GENERIC'),
            })}
          </p>
        ) : null}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('confirm')}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
